import * as React from 'react';
import * as MUI from '@locus-taxy/encore-mui';
import * as Network from '@locus-taxy/encore-ui-network';
import * as Toolkit from '@locus-taxy/encore-ui-toolkit';
import * as I18n from '@locus-taxy/encore-i18n-toolkit';
import * as Form from '@locus-taxy/encore-form';
import * as Utils from '@locus-taxy/encore-utils';
import * as EncoreGrid from '@locus-taxy/encore-grid';
import { Box } from '@locus-taxy/encore-mui';
import { createInstance } from '@module-federation/enhanced/runtime';
import ReactDOM from 'react-dom';
import * as ReactRouterDOM from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import type { EntityDetailsBodyProps } from './types';
import { CUSTOM_APP_BASE_URL } from '../../config';
import { getTestId } from '@locus-taxy/encore-utils';
import { useRouteContext } from '../../hooks/useRouteContext';
import { TestIdAction } from '../../utils/testIdUtils';

type ShipmentDetailsProps = {
  clientId?: string;
  entityName?: string;
  entityTitle?: string;
  rootInstanceKey?: string;
  entityData?: Record<string, unknown>;
  detailsForm?: unknown;
  showTimeline?: boolean;
  entitySchemaInfo?: unknown;
};

type ShipmentDetailsRemoteModule = {
  default: React.ComponentType<ShipmentDetailsProps>;
};

const mf = createInstance({
  name: 'host',
  remotes: [
    {
      name: 'shipment-details',
      entry: `${CUSTOM_APP_BASE_URL}/shipment-details/mf-manifest.json`,
    },
  ],
  shared: {
    react: {
      version: '19.1.1',
      scope: 'default',
      lib: () => React,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.1.1',
      },
    },
    'react-dom': {
      version: '19.1.1',
      scope: 'default',
      lib: () => ReactDOM,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.1.1',
      },
    },
    'react-router-dom': {
      version: '7.6.3',
      scope: 'default',
      lib: () => ReactRouterDOM,
      shareConfig: {
        singleton: true,
        requiredVersion: '^7.6.3',
      },
    },
    '@locus-taxy/encore-mui': {
      version: '1.0.0-beta.28',
      scope: 'default',
      lib: () => MUI,
      shareConfig: {
        singleton: true,
        requiredVersion: '^1.0.0-beta.28',
      },
    },
    '@locus-taxy/encore-ui-network': {
      version: '3.1.0',
      scope: 'default',
      lib: () => Network,
      shareConfig: {
        singleton: true,
        requiredVersion: '^3.1.0',
      },
    },
    '@locus-taxy/encore-grid': {
      version: '2.0.0',
      scope: 'default',
      lib: () => EncoreGrid,
      shareConfig: {
        singleton: true,
        requiredVersion: '^2.0.0',
      },
    },
    '@locus-taxy/encore-ui-toolkit': {
      version: '10.0.0',
      scope: 'default',
      lib: () => Toolkit,
      shareConfig: {
        singleton: true,
        requiredVersion: '^10.0.0',
      },
    },
    '@locus-taxy/encore-i18n-toolkit': {
      version: '2.0.0',
      scope: 'default',
      lib: () => I18n,
      shareConfig: {
        singleton: true,
        requiredVersion: '^2.0.0',
      },
    },
    '@locus-taxy/encore-form': {
      version: '1.0.0',
      scope: 'default',
      lib: () => Form,
      shareConfig: {
        singleton: true,
        requiredVersion: '^1.0.0',
      },
    },
    '@locus-taxy/encore-utils': {
      version: '0.1.3',
      scope: 'default',
      lib: () => Utils,
      shareConfig: {
        singleton: true,
        requiredVersion: '^0.1.3',
      },
    },
  },
});

const ShipmentDetailsLazy = React.lazy(() => {
  return mf.loadRemote('shipment-details/App').then((value: unknown) => {
    const { default: DefaultExport } = value as ShipmentDetailsRemoteModule;
    const Component = DefaultExport;
    return { default: Component as React.ComponentType<ShipmentDetailsProps> };
  });
});

export const ShipmentDetailsContainer = ({
  clientId,
  entityName,
  entityTitle,
  rootInstanceKey,
  entityData,
  detailsForm,
  showTimeline,
  remountKey,
  entitySchemaInfo,
}: EntityDetailsBodyProps & { remountKey?: number }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { entity, page } = useRouteContext();

  return (
    <Box
      ref={containerRef}
      sx={{
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
      }}
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'shipment-details-container',
        entity: entity ?? 'shipment',
        page: page ?? 'details',
      })}
    >
      <React.Suspense fallback={<LoadingSpinner />}>
        <ShipmentDetailsLazy
          key={remountKey}
          clientId={clientId}
          entityName={entityName}
          entityTitle={entityTitle}
          rootInstanceKey={rootInstanceKey}
          entityData={entityData}
          detailsForm={detailsForm}
          showTimeline={showTimeline}
          entitySchemaInfo={entitySchemaInfo}
        />
      </React.Suspense>
    </Box>
  );
};
